import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Lang, changeLanguage, currentLang, getActiveLangs, onActiveLangsChange, LANG_LABELS } from '../i18n';
import { t } from '../i18n';

// ============================================
// LanguageContext (2026-09-17; weitere Sprachen 31.10.2026) – nach dem Muster von ThemeContext.
// Ein Sprachwechsel speichert die Wahl und lädt die Seite neu (siehe i18n/index.ts),
// damit auch Label-Konstanten auf Modulebene in der neuen Sprache stehen.
// activeLangs = Sprachen im Umschalter (Server /api/i18n/languages, Start de,en).
// ============================================

interface LanguageContextValue {
  lang: Lang;
  /** Aktive Sprachen in Anzeige-Reihenfolge (mindestens de). */
  activeLangs: Lang[];
  /** Eigenbezeichnung einer Sprache ('pl' → 'Polski'). */
  labelFor: (lang: Lang) => string;
  setLang: (lang: Lang) => Promise<void>;
  /** Zur nächsten aktiven Sprache wechseln (Kompatibilität; bei zwei Sprachen = de↔en). */
  toggleLang: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => currentLang());
  const [activeLangs, setActive] = useState<Lang[]>(() => getActiveLangs());

  useEffect(() => onActiveLangsChange(setActive), []);

  const setLang = useCallback(async (next: Lang) => {
    if (next === lang) return;
    await changeLanguage(next);
    setLangState(next);
  }, [lang]);

  const toggleLang = useCallback(() => {
    const list = activeLangs.length ? activeLangs : ['de', 'en'] as Lang[];
    const idx = list.indexOf(lang);
    return setLang(list[(idx + 1) % list.length]);
  }, [lang, activeLangs, setLang]);

  const labelFor = useCallback((l: Lang) => LANG_LABELS[l] ?? l, []);

  const value = useMemo(() => ({ lang, activeLangs, labelFor, setLang, toggleLang }), [lang, activeLangs, labelFor, setLang, toggleLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Sprachkontext OHNE Pflicht-Provider (01.11.2026, International-SEO): Link-Bausteine (Startseite, Karten,
 * Teaser) wollen die Oberflächensprache nur als Standard für Sprach-URLs – außerhalb eines Providers (Tests,
 * eingebettete Bausteine) liefern sie null und die Links bleiben deutsch.
 */
export const useOptionalLanguage = (): LanguageContextValue | null => useContext(LanguageContext) ?? null;

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error(t('web.context.language-context.uselanguage-muss-innerhalb-von-languagep'));
  return ctx;
};
